<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';

import { eqsApi } from '@/api/eqs';
import OrgTreePicker from '@/components/OrgTreePicker.vue';
import PersonPicker from '@/components/PersonPicker.vue';
import { notify } from '@/services/notice';
import type {
  Project,
  ProjectInitializationPreview,
  ProjectInitializationRequest,
} from '@/types/domain';
import { errorMessage, formatDate } from '@/utils/format';
import { personLabel, type PickablePerson } from '@/utils/person';
import { PROJECT_TYPES } from '@/utils/project-types';

const emit = defineEmits<{
  cancel: [];
  created: [project: Project];
}>();

const phaseNames: Record<string, string> = {
  P01: '项目计划与立项',
  P02: '产品设计与初始过程开发',
  P03: '产品和过程开发',
  P04: '产品和过程确认',
  P05: '批量生产',
  P06: '持续改进与关闭',
};

type ProcessScope = ProjectInitializationRequest['applicableProcesses'][number];
type ToolingScope = NonNullable<ProjectInitializationRequest['applicableTooling']>[number];

const processOptions: Array<{ value: ProcessScope; label: string }> = [
  { value: 'injection', label: '注塑' },
  { value: 'painting', label: '喷漆' },
  { value: 'electroplating', label: '电镀' },
  { value: 'assembly', label: '装配' },
];
const toolingOptions: Array<{ value: ToolingScope; label: string }> = [
  { value: 'mold', label: '模具' },
  { value: 'fixtures', label: '工装' },
  { value: 'gauges', label: '检具' },
  { value: 'purchased_parts', label: '外购件开发' },
];
const step = ref(1);
const projectManagers = ref<PickablePerson[]>([]);
const managerLoading = ref(false);
const managerError = ref('');
/*
  项目团队。
  试用反馈 0813：「项目创建只输入团队成员，但审批流里有主管领导和中高层，
  不输入就会报错」——人当时就知道该配谁，只是没地方填，
  等提交审批被拦住才回头补。这一步就是把那次往返省掉。
*/
interface TeamPick { userId: number; displayName: string; orgName: string | null; roleTitle: string }
const team = ref<TeamPick[]>([]);
const roleOptions = ref<string[]>([]);
const approvalRoles = ref<Set<string>>(new Set());
const orgPickerOpen = ref(false);
/*
  选完人要把输入框清空。
  原来选中后名字一直留在框里，看着像「这个值还挂着」，再加下一个人得先手动清掉——
  Tony 2026-08-19：「下拉选项我选择好了之后又要重新点一下」。
  PersonPicker 的文本是内部状态，换 key 重挂是最省事又不改它契约的做法。
*/
const pickerKey = ref(0);

/* 审批流点名要的排在前面：建项目时真正要配的就是这批，别让人在几十个里翻 */
const approvalRoleList = computed(() =>
  roleOptions.value.filter((role) => approvalRoles.value.has(role)));
const otherRoleList = computed(() =>
  roleOptions.value.filter((role) => !approvalRoles.value.has(role)));

/** 审批流点名要、但团队里还没人担的角色——建完就报错的那批，提前说 */
const missingApprovalRoles = computed(() => {
  const taken = new Set(team.value.map((member) => member.roleTitle));
  if (form.value.projectManagerUserId) taken.add('项目经理');
  return [...approvalRoles.value].filter((role) => !taken.has(role));
});

function addTeamMember(person: { id: number; displayName: string; orgName?: string | null }): void {
  if (team.value.some((member) => member.userId === person.id)) {
    notify('error', '已经在名单里', `${person.displayName} 已经加过了，改角色直接在右边选。`);
    return;
  }
  team.value.push({
    userId: person.id,
    displayName: person.displayName,
    orgName: person.orgName ?? null,
    roleTitle: '',
  });
  pickerKey.value += 1;
}

function removeTeamMember(userId: number): void {
  team.value = team.value.filter((member) => member.userId !== userId);
}

async function loadRoleOptions(): Promise<void> {
  try {
    const payload = await eqsApi.projectRoles();
    const roles = (payload.roles ?? []) as Array<{ role: string; inApproval: boolean }>;
    roleOptions.value = roles.map((item) => item.role);
    approvalRoles.value = new Set(roles.filter((item) => item.inApproval).map((item) => item.role));
  } catch {
    // 取不到就退回自由输入；建项目不该因为一个下拉挂掉
    roleOptions.value = [];
  }
}

const preview = ref<ProjectInitializationPreview | null>(null);
const previewLoading = ref(false);
const creating = ref(false);
const confirmed = ref(false);
const idempotencyKey = `project-init-${globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`}`;

const form = ref({
  projectNo: '',
  projectName: '',
  customer: '',
  projectManagerUserId: null as number | null,
  projectType: '自主设计',
  priority: 'normal',
  startDate: '',
  dataFreezeDate: '',
  t0Date: '',
  internalT0Date: '',
  otsDate: '',
  ppapDate: '',
  sopDate: '',
  handoverDate: '',
  closeDate: '',
  processScopes: [] as ProcessScope[],
  toolingScopes: [] as ToolingScope[],
  description: '',
});

const managerName = computed(() => {
  const manager = projectManagers.value.find((item) => item.id === form.value.projectManagerUserId);
  return manager ? personLabel(manager) : '尚未指定';
});

const selectedProcessNames = computed(() => form.value.processScopes
  .map((value) => processOptions.find((item) => item.value === value)?.label || value)
  .join(' / '));

const localWarnings = computed(() => {
  const warnings: string[] = [];
  if (!form.value.otsDate) warnings.push('未设置 OTS 日期，数据冻结、T0 和内部 OTS 批准将按模板默认比例推导。');
  if (!form.value.toolingScopes.length) warnings.push('未选择开发内容，模具、工装、检具和外购件专属任务将标记为不适用。');
  return warnings;
});

const requestPayload = computed<ProjectInitializationRequest>(() => ({
  projectNo: form.value.projectNo.trim(),
  projectName: form.value.projectName.trim(),
  customer: nullable(form.value.customer),
  projectManagerUserId: form.value.projectManagerUserId,
  projectType: nullable(form.value.projectType),
  priority: nullable(form.value.priority),
  startDate: nullable(form.value.startDate),
  endDate: nullable(form.value.closeDate),
  description: nullable(form.value.description),
  status: 'draft',
  formal: true,
  initializeFromStandardTemplate: true,
  targetOtsDate: nullable(form.value.otsDate),
  targetPpapDate: form.value.ppapDate,
  targetSopDate: form.value.sopDate,
  targetCloseDate: nullable(form.value.closeDate),
  targetDataFreezeDate: nullable(form.value.dataFreezeDate),
  targetT0Date: nullable(form.value.t0Date),
  targetInternalT0Date: nullable(form.value.internalT0Date),
  targetHandoverDate: nullable(form.value.handoverDate),
  applicableProcesses: [...form.value.processScopes],
  applicableTooling: [...form.value.toolingScopes],
  idempotencyKey,
  teamMembers: team.value
    .filter((member) => member.roleTitle.trim())
    .map((member) => ({ userId: member.userId, roleTitle: member.roleTitle.trim() })),
}));

function nullable(value: string): string | null {
  const normalized = value.trim();
  return normalized || null;
}

function firstStepError(): string {
  if (!form.value.projectNo.trim()) return '请填写项目编号。';
  if (!form.value.projectName.trim()) return '请填写项目名称。';
  if (!form.value.customer.trim()) return '请填写客户。';
  if (!form.value.projectManagerUserId) return '请选择项目经理。';
  if (!form.value.startDate) return '请选择项目启动日期。';
  return '';
}

function secondStepError(): string {
  if (!form.value.processScopes.length) return '请至少选择一种适用工艺。';
  if (!form.value.ppapDate) return '请填写目标 PPAP 日期。';
  if (!form.value.sopDate) return '请填写目标 SOP 日期。';
  if (!form.value.closeDate) return '请填写预计关闭日期。';
  const dates = [
    ['项目启动', form.value.startDate],
    ['数据冻结', form.value.dataFreezeDate],
    ['T0 首次试模', form.value.t0Date],
    ['OTS', form.value.otsDate],
    ['PPAP', form.value.ppapDate],
    ['SOP', form.value.sopDate],
    ['项目移交', form.value.handoverDate],
    ['项目关闭', form.value.closeDate],
  ].filter((item) => item[1]) as string[][];
  for (let index = 1; index < dates.length; index += 1) {
    if (dates[index][1] < dates[index - 1][1]) {
      return `${dates[index][0]}日期不能早于${dates[index - 1][0]}日期。`;
    }
  }
  return '';
}

function continueToScope() {
  const message = firstStepError();
  if (message) {
    notify('error', '关键信息未完成', message);
    return;
  }
  step.value = 2;
}

/** 客户清单（company.xlsx 的 64 家）；拿不到就退回自由输入，不拦人 */
const customerOptions = ref<Array<{ value: string; label: string }>>([]);
async function loadCustomerOptions(): Promise<void> {
  try {
    const options = await eqsApi.formOptions();
    customerOptions.value = options.customers ?? [];
  } catch {
    customerOptions.value = [];   // 拿不到清单就退回自由输入，不拦人
  }
}

/**
 * 项目经理候选人。
 *
 * 走 /people（专门给「选人」用的通讯录），不再走 modules/hr/users。
 * 后者是通用列表接口，**默认只给 50 条**：库里 109 个在职账号，
 * 下拉里少了一多半，而且不报错——人只会以为「这人没进系统」。
 *
 * 顺带修掉另一个错：原来的过滤是「排除 inactive/closed」，
 * 于是 31 条 `reclassified_org` 会被放行——那些根本不是人，
 * 是「研发中心」「质量部」「决策委员会」这类**部门名被当账号导进来了**
 * （零节点、零成员、零登录）。/people 只认 status='active'，天然没这问题。
 */
async function loadProjectManagers() {
  managerLoading.value = true;
  managerError.value = '';
  try {
    const response = await eqsApi.people();
    const people = Array.isArray(response.people) ? response.people : [];
    projectManagers.value = (people as PickablePerson[])
      .slice()
      .sort((a, b) => a.displayName.localeCompare(b.displayName, 'zh-CN'));
  } catch (error) {
    projectManagers.value = [];
    managerError.value = errorMessage(error);
  } finally {
    managerLoading.value = false;
  }
}

/** 打字没打中人的时候说清楚，不要默默清空 */
function onManagerUnresolved(text: string): void {
  notify(
    'error',
    '没找到这个人',
    `通讯录里没有「${text}」，或者同名的有多个。`
    + '先删掉重打两个字看提示里的候选；确认是新同事的话，要先在「用户管理」里给他开账号。',
  );
}

/** 第 2 步过了就进团队步；范围/里程碑的校验仍在这里做，别拖到预览才报 */
function continueToTeam(): void {
  const message = secondStepError();
  if (message) {
    notify('error', '还差一点', message);
    return;
  }
  void loadRoleOptions();
  step.value = 3;
}

/** 打字选中的人 */
function onTeamPersonPicked(userId: number | null): void {
  if (userId === null) return;
  const person = projectManagers.value.find((item) => item.id === userId);
  if (person) addTeamMember({ id: person.id, displayName: person.displayName, orgName: person.orgName });
}

/** 部门树上点的人 */
function onOrgTreePicked(
  userId: number | null,
  member: { id: number; displayName: string } | null,
): void {
  if (!member) return;
  addTeamMember({ id: member.id, displayName: member.displayName, orgName: null });
}

async function generatePreview() {
  const message = secondStepError();
  if (message) {
    notify('error', '生成条件未完成', message);
    return;
  }
  previewLoading.value = true;
  try {
    preview.value = await eqsApi.previewProjectInitialization(requestPayload.value);
    confirmed.value = false;
    step.value = 4;
  } catch (error) {
    notify('error', '预览生成失败', errorMessage(error));
  } finally {
    previewLoading.value = false;
  }
}

async function createProject() {
  if (!confirmed.value) {
    notify('error', '请先确认生成内容', '确认后系统才会正式创建项目和任务。');
    return;
  }
  creating.value = true;
  try {
    const result = await eqsApi.initializeProject(requestPayload.value);
    notify(
      'success',
      '项目初始化完成',
      `已生成 ${result.initialization.phaseCount} 个阶段、${result.initialization.gateCount} 个 Gate 和 ${result.initialization.taskCount} 项任务。`,
    );
    /*
      这个项目号之前用过（同号的老项目已关掉）—— 也就是「删了重建」。
      得当场说一句：老项目上的在跑变更、有正文的文件、M-BOM 不会自己跟过来，
      是系统在后台接过来的。不说的话人会以为「新项目里什么都没有」是丢了数据，
      于是自己再手工搬一遍（0916 的 DH26005 就这么搬了一天多）。
    */
    const rebuiltFrom = Array.isArray(result.rebuiltFrom) ? result.rebuiltFrom : [];
    if (rebuiltFrom.length) {
      const names = rebuiltFrom
        .map((item) => `${item.projectName ?? ''}（#${item.id}）`)
        .join('、');
      notify(
        'info',
        '这个项目号之前用过，老项目上的东西正在接过来',
        `同号的老项目：${names}。它身上还在跑的工程变更、有正文的交付物文件会自动`
        + '接到这个新项目下，M-BOM 会按新项目的 EBOM 重新生成一份——一两分钟内完成，'
        + '接完会给项目经理发通知。老项目本身保留不动，随时能回头核对。',
      );
    }
    emit('created', result);
  } catch (error) {
    notify('error', '项目创建失败', errorMessage(error));
  } finally {
    creating.value = false;
  }
}

onMounted(() => { void loadProjectManagers(); void loadCustomerOptions(); void loadRoleOptions(); });
</script>

<template>
  <section class="project-wizard">
    <header class="wizard-header">
      <div>
        <span>NEW PROJECT / APQP INITIALIZATION</span>
        <h2>创建项目并生成完整计划</h2>
        <p>只录入关键条件，系统按标准模板生成 P01—P06、13 个里程碑（GATE1—GATE6 与 OTS/PPAP/SOP 等客户与内部节点）、任务和交付物。</p>
      </div>
      <button class="inline-button" type="button" @click="emit('cancel')">取消创建</button>
    </header>

    <ol class="wizard-steps" aria-label="项目创建步骤">
      <li :class="{ active: step === 1, completed: step > 1 }"><b>01</b><span>关键信息</span></li>
      <li :class="{ active: step === 2, completed: step > 2 }"><b>02</b><span>范围与里程碑</span></li>
      <li :class="{ active: step === 3, completed: step > 3 }"><b>03</b><span>项目团队</span></li>
      <li :class="{ active: step === 4 }"><b>04</b><span>预览并确认</span></li>
    </ol>

    <form v-if="step === 1" class="wizard-form" @submit.prevent="continueToScope">
      <div class="form-section-title">
        <span>PROJECT IDENTITY</span>
        <h3>先确定项目身份和负责人</h3>
      </div>
      <div class="wizard-fields">
        <label><span>项目编号 *</span><input v-model="form.projectNo" type="text" placeholder="例如：X04-C" /></label>
        <label class="field-wide"><span>项目名称 *</span><input v-model="form.projectName" type="text" placeholder="填写客户可识别的项目名称" /></label>
        <label>
          <span>客户 *</span>
          <!-- 客户清单来自 company.xlsx（64 家）；打字筛选，新客户可直接输入 -->
          <input v-model="form.customer" type="text" list="wizard-customer-list" autocomplete="off" placeholder="打字筛选客户清单" />
          <datalist id="wizard-customer-list">
            <option v-for="option in customerOptions" :key="option.value" :value="option.value">{{ option.label }}</option>
          </datalist>
        </label>
        <label>
          <span>项目经理 *</span>
          <!-- 打字即筛选：109 个人用原生下拉一个个翻，谁也找不到 -->
          <PersonPicker
            v-model="form.projectManagerUserId"
            :people="projectManagers"
            :disabled="managerLoading"
            label="项目经理"
            list-id="wizard-manager-list"
            @unresolved="onManagerUnresolved"
          />
          <datalist id="wizard-manager-list">
            <option v-for="manager in projectManagers" :key="manager.id" :value="personLabel(manager)" />
          </datalist>
          <small v-if="managerError" class="field-error">
            人员读取失败。<button class="text-button" type="button" @click="loadProjectManagers">重试</button>
          </small>
          <small v-else class="field-hint">
            输入姓名即可筛选，共 {{ projectManagers.length }} 人；找不到说明还没开账号。
          </small>
        </label>
        <label>
          <span>项目类型 *</span>
          <select v-model="form.projectType">
            <option v-for="type in PROJECT_TYPES" :key="type.value" :value="type.value">{{ type.value }}</option>
          </select>
        </label>
        <label>
          <span>优先级</span>
          <select v-model="form.priority">
            <option value="critical">紧急</option>
            <option value="high">高</option>
            <option value="normal">普通</option>
            <option value="low">低</option>
          </select>
        </label>
        <label><span>项目启动日期 *</span><input v-model="form.startDate" type="date" /></label>
        <label class="field-full"><span>项目说明</span><textarea v-model="form.description" rows="2" placeholder="可填写范围、客户要求或特殊约束"></textarea></label>
      </div>
      <footer class="wizard-actions">
        <span>正式项目将固定生成 GATE1—GATE6。</span>
        <button class="button button-primary" type="submit">下一步：设置范围</button>
      </footer>
    </form>

    <form v-else-if="step === 2" class="wizard-form" @submit.prevent="continueToTeam">
      <div class="scope-layout">
        <div>
          <div class="form-section-title">
            <span>APPLICABILITY</span>
            <h3>选择适用工艺和开发内容</h3>
          </div>
          <fieldset class="choice-group">
            <legend>适用工艺 *</legend>
            <label v-for="option in processOptions" :key="option.value">
              <input v-model="form.processScopes" type="checkbox" :value="option.value" />
              <span>{{ option.label }}</span>
            </label>
          </fieldset>
          <fieldset class="choice-group">
            <legend>开发内容</legend>
            <label v-for="option in toolingOptions" :key="option.value">
              <input v-model="form.toolingScopes" type="checkbox" :value="option.value" />
              <span>{{ option.label }}</span>
            </label>
          </fieldset>
        </div>
        <div>
          <div class="form-section-title">
            <span>KEY DATES</span>
            <h3>输入关键日期，系统自动倒排任务</h3>
          </div>
          <!--
            按「谁在等这个日子」分两组：客户节点误期要对外解释，
            内部节点是自己卡的（试用反馈 0818）。客户组排前面。
          -->
          <p class="ms-band ms-band-customer">
            <b>客户节点</b><small>对客户承诺的日子，误期要对外解释</small>
          </p>
          <div class="milestone-fields">
            <label><span>T0 首次试模</span><input v-model="form.t0Date" type="date" /></label>
            <label><span>目标 OTS</span><input v-model="form.otsDate" type="date" /></label>
            <label><span>目标 PPAP *</span><input v-model="form.ppapDate" type="date" /></label>
            <label><span>目标 SOP *</span><input v-model="form.sopDate" type="date" /></label>
          </div>
          <p class="ms-band">
            <b>内部节点</b><small>我们自己卡的，用来保住上面那几个</small>
          </p>
          <div class="milestone-fields">
            <label><span>项目启动</span><input v-model="form.startDate" type="date" /></label>
            <label><span>数据冻结 GATE2</span><input v-model="form.dataFreezeDate" type="date" /></label>
            <label>
              <span>T0 内部试模确认</span>
              <input v-model="form.internalT0Date" type="date" />
            </label>
            <label><span>项目移交 GATE5</span><input v-model="form.handoverDate" type="date" /></label>
            <label><span>预计关闭 *</span><input v-model="form.closeDate" type="date" /></label>
          </div>
          <p class="milestone-hint">
            内部节点里还会自动生成 OTS / PPAP / SOP 的内部对应节点和量产准备——
            每个客户节点前面都有一道内部关口。
            <b>T0 内部试模确认</b>留空时建议排在客户 T0 前 7 天，那只是建议：
            节奏紧的时候填成和客户 T0 <b>同一天</b>也可以。
            留空的日期由系统按历史项目比例推导，预览中标「系统建议」，请项目经理确认后再创建。
          </p>
          <aside class="gate-rule">
            <b>固定控制点</b>
            <span>GATE4 = PPAP 批准</span>
            <span>GATE6 = 项目关闭批准</span>
          </aside>
        </div>
      </div>
      <footer class="wizard-actions">
        <button class="button button-secondary" type="button" @click="step = 1">返回修改</button>
        <button class="button button-primary" type="submit">下一步：配团队</button>
      </footer>
    </form>

    <form v-else-if="step === 3" class="wizard-form wizard-team" @submit.prevent="generatePreview">
      <div class="team-intro">
        <b>把这个项目要用到的人一次配齐</b>
        <span>
          审批和评审都是<strong>按角色找人</strong>的：谁在这里担了「质量工程师」，
          提交审批时系统才派得到他。建完再补也行，但那时人已经被拦在提交那一步了。
        </span>
      </div>

      <!-- 两种找人方式并排：知道名字就打字，只知道部门就点树 -->
      <div class="team-pickers">
        <label class="team-search">
          <span>知道名字，直接打字</span>
          <PersonPicker
            :key="pickerKey"
            :people="projectManagers"
            :model-value="null"
            label="搜索团队成员"
            list-id="wizard-team-options"
            @update:model-value="onTeamPersonPicked"
            @unresolved="onManagerUnresolved"
          />
          <datalist id="wizard-team-options">
            <option v-for="person in projectManagers" :key="person.id" :value="personLabel(person)" />
          </datalist>
          <small v-if="managerError" class="team-warn">通讯录读取失败：{{ managerError }}</small>
        </label>
        <div class="team-org">
          <button type="button" class="button button-secondary" @click="orgPickerOpen = !orgPickerOpen">
            {{ orgPickerOpen ? '收起组织架构' : '按部门找人' }}
          </button>
          <small>只知道是哪个部门的时候用这棵树，点人名即可加进名单。</small>
        </div>
      </div>
      <OrgTreePicker
        v-if="orgPickerOpen"
        :model-value="null"
        @update:model-value="onOrgTreePicked"
      />

      <div v-if="team.length" class="team-list">
        <article v-for="member in team" :key="member.userId">
          <div class="team-who">
            <b>{{ member.displayName }}</b>
            <small>{{ member.orgName || '未挂部门' }}</small>
          </div>
          <!--
            角色用原生下拉，不用「打字筛选 + datalist」。
            人员那边有 109 个，必须能打字；角色只有几十个且是**受控词表**——
            审批是按角色名精确匹配派人的，打错一个字就派不到人。
            下拉点一下就定，也不会出现「选完还要再点一次」（Tony 2026-08-19）。
          -->
          <label class="team-role">
            <span>担任角色 *</span>
            <select v-model="member.roleTitle">
              <option value="">请选择</option>
              <optgroup v-if="approvalRoleList.length" label="审批流会用到的角色">
                <option v-for="role in approvalRoleList" :key="role" :value="role">{{ role }}</option>
              </optgroup>
              <optgroup v-if="otherRoleList.length" label="其他在用角色">
                <option v-for="role in otherRoleList" :key="role" :value="role">{{ role }}</option>
              </optgroup>
            </select>
          </label>
          <button type="button" class="inline-button" @click="removeTeamMember(member.userId)">移除</button>
        </article>
      </div>
      <p v-else class="team-empty">
        还没加人。可以先跳过——但审批流点名要的角色没人担的话，
        提交审批时会被挡下来，那时还得回来补。
      </p>

      <!--
        审批流要谁、团队里缺谁，建之前就说清楚。
        0813 反馈里那条「不输入就会报错」，报错发生在提交审批那一刻，
        离建项目已经隔了好几天，人早忘了当时该配谁。
      -->
      <aside v-if="missingApprovalRoles.length" class="team-gap">
        <b>审批流会用到、但现在没人担的角色（{{ missingApprovalRoles.length }}）</b>
        <span>{{ missingApprovalRoles.join('、') }}</span>
        <small>不补也能建项目；等这些角色要签字时，系统会拦在提交那一步。</small>
      </aside>

      <footer class="wizard-actions">
        <button class="button button-secondary" type="button" @click="step = 3">返回修改</button>
        <button class="button button-primary" type="submit" :disabled="previewLoading">
          {{ previewLoading ? '正在生成预览…' : '生成计划预览' }}
        </button>
      </footer>
    </form>

    <section v-else-if="step === 4 && preview" class="wizard-preview">
      <div class="preview-summary">
        <div><span>模板版本</span><strong>{{ preview.template.revision }}</strong></div>
        <div><span>阶段</span><strong>{{ preview.totals.phaseCount }}</strong></div>
        <div><span>任务</span><strong>{{ preview.totals.taskCount }}</strong></div>
        <div><span>关键交付</span><strong>{{ preview.totals.keyDeliverableCount }}</strong></div>
      </div>

      <div class="preview-context">
        <b>{{ form.projectNo }} · {{ form.projectName }}</b>
        <span>{{ form.customer }} · {{ managerName }} · {{ selectedProcessNames }}</span>
      </div>

      <ol class="gate-preview" aria-label="项目里程碑">
        <li v-for="gate in preview.gates" :key="gate.code" :class="`kind-${gate.kind}`">
          <b>{{ gate.code }}</b>
          <span>
            {{ gate.title }}
            <em v-if="gate.kind === 'customer'" class="kind-tag customer">客户节点</em>
            <em v-else-if="gate.kind === 'internal'" class="kind-tag internal">内部节点</em>
          </span>
          <time :class="{ derived: gate.derived }">
            {{ formatDate(gate.planDate) }}
            <small v-if="gate.derived">系统建议</small>
          </time>
        </li>
      </ol>
      <p v-if="preview.totals.derivedMilestoneCodes.length" class="derived-hint">
        以下 {{ preview.totals.derivedMilestoneCodes.length }} 个日期由系统推导，创建后请在项目计划中确认：
        {{ preview.totals.derivedMilestoneCodes.join('、') }}
      </p>

      <ol class="stage-preview">
        <li v-for="phase in preview.phases" :key="phase.code">
          <div><b>{{ phase.code }}</b><span>{{ phase.name || phaseNames[phase.code] || '阶段任务' }}</span></div>
          <small>{{ phase.taskCount }}项任务 · {{ phase.keyDeliverableCount }}项关键交付</small>
          <time>{{ formatDate(phase.plannedStartAt) }} — {{ formatDate(phase.plannedEndAt) }}</time>
        </li>
      </ol>

      <aside v-if="localWarnings.length" class="preview-warnings">
        <b>生成前请确认</b>
        <span v-for="warning in localWarnings" :key="warning">{{ warning }}</span>
      </aside>

      <label class="confirmation">
        <input v-model="confirmed" type="checkbox" />
        <span>我已核对项目、负责人、适用范围和关键日期；确认创建项目并生成计划。</span>
      </label>
      <footer class="wizard-actions">
        <button class="button button-secondary" type="button" @click="step = 2">返回修改</button>
        <button class="button button-primary" type="button" :disabled="creating || !confirmed" @click="createProject">
          {{ creating ? '正在创建完整项目…' : '确认创建项目' }}
        </button>
      </footer>
    </section>
  </section>
</template>

<style scoped>
.project-wizard {
  border: 1px solid var(--line);
  background:
    linear-gradient(135deg, rgba(18, 109, 166, .06), transparent 34%),
    var(--white);
}

.wizard-header {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  padding: 22px 24px 18px;
  border-bottom: 1px solid var(--line);
}

.wizard-header span,
.form-section-title span {
  color: var(--blue);
  font: 800 10px var(--display);
  letter-spacing: .12em;
}

.wizard-header h2,
.form-section-title h3 {
  margin: 4px 0 0;
  color: var(--ink);
  font-family: var(--display);
}

.wizard-header h2 { font-size: 26px; }
.wizard-header p { margin: 6px 0 0; color: var(--muted); font-size: 13px; }

.wizard-team { display: grid; gap: 14px; }
.team-intro { display: grid; gap: 4px; padding: 10px 12px; border-left: 3px solid var(--blue); background: rgba(18, 109, 166, .05); }
.team-intro b { font-size: 13px; }
.team-intro span { color: var(--muted); font-size: 12px; line-height: 1.7; }
.team-pickers { display: grid; grid-template-columns: minmax(240px, 1fr) auto; gap: 12px; align-items: end; }
.team-search { display: grid; gap: 4px; min-width: 0; }
.team-search > span { font-size: 12px; font-weight: 700; }
.team-org { display: grid; gap: 4px; }
.team-org small, .team-warn { color: var(--muted); font-size: 12px; }
.team-list { display: grid; gap: 8px; }
.team-list article { display: grid; grid-template-columns: minmax(120px, 1fr) minmax(180px, 1.4fr) auto; gap: 10px; align-items: end; padding: 8px 10px; border: 1px solid var(--line); background: var(--white); }
.team-who { display: grid; gap: 2px; min-width: 0; }
.team-who b { font-size: 13px; }
.team-who small { color: var(--muted); font-size: 12px; }
.team-role { display: grid; gap: 3px; min-width: 0; }
.team-role > span { font-size: 12px; font-weight: 700; }
.team-role select { width: 100%; padding: 6px 8px; border: 1px solid var(--line); background: var(--white); font: inherit; font-size: 12px; }
.team-empty { margin: 0; color: var(--muted); font-size: 12px; line-height: 1.7; }
.team-gap { display: grid; gap: 4px; padding: 9px 12px; border-left: 3px solid #d98324; background: #fdf6ec; }
.team-gap b { font-size: 12px; }
.team-gap span { font-size: 12px; }
.team-gap small { color: var(--muted); font-size: 12px; }

@media (max-width: 760px) {
  .team-pickers { grid-template-columns: 1fr; }
  .team-list article { grid-template-columns: 1fr; }
}

.wizard-steps {
  display: grid;
  /* 有几步就几列。原来写死 repeat(3, 1fr)，加第 4 步「项目团队」时被挤成一团——
     以后再增减步骤不用回头改这里 */
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  margin: 0;
  padding: 0;
  border-bottom: 1px solid var(--line);
  list-style: none;
}

.wizard-steps li {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 58px;
  padding: 0 16px;
  border-left: 1px solid var(--line);
  color: var(--muted);
  min-width: 0;
}

.wizard-steps li:first-child { border-left: 0; }
.wizard-steps b { font: 800 12px var(--display); }
.wizard-steps span { font-size: 13px; font-weight: 700; }
.wizard-steps .active { color: var(--white); background: var(--blue); }
.wizard-steps .completed { color: var(--blue); background: rgba(18, 109, 166, .05); }

.wizard-form,
.wizard-preview { padding: 24px; }
.form-section-title { margin-bottom: 16px; }
.form-section-title h3 { font-size: 19px; }
.wizard-fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.wizard-fields label,
.milestone-fields label {
  display: grid;
  gap: 6px;
}

.wizard-fields label > span,
.milestone-fields label > span,
.choice-group legend {
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
}

.wizard-fields input,
.wizard-fields select,
.wizard-fields textarea,
.milestone-fields input {
  width: 100%;
  border: 1px solid var(--line);
  color: var(--ink);
  background: var(--white);
  outline: 0;
}

.wizard-fields input,
.wizard-fields select,
.milestone-fields input { height: 42px; padding: 0 11px; }
.wizard-fields textarea { padding: 10px 11px; resize: vertical; }
/* 字段下面的一行说明：说清楚共几个人、找不到该去哪儿开账号 */
.field-hint { color: var(--muted); font-size: 12px; line-height: 1.5; }
.wizard-fields input:focus,
.wizard-fields select:focus,
.wizard-fields textarea:focus,
.milestone-fields input:focus { border-color: var(--blue); box-shadow: 0 0 0 3px rgba(18, 109, 166, .1); }
.field-wide { grid-column: span 2; }
.field-full { grid-column: 1 / -1; }
.field-error { color: var(--risk); font-size: 12px; }
.text-button { min-height: 24px; display: inline-flex; align-items: center; padding: 0 8px; border: 1px solid var(--line-strong); border-radius: var(--radius-s); background: var(--white); cursor: pointer; font: inherit; font-size: 12px; font-weight: 700; line-height: 1; white-space: nowrap; color: var(--blue); }
.text-button:hover { border-color: var(--blue); background: rgba(0,117,174,.07); }

.wizard-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid var(--line);
}

.wizard-actions > span { margin-right: auto; color: var(--muted); font-size: 12px; }
.scope-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 34px; }
.choice-group { display: flex; flex-wrap: wrap; gap: 8px; margin: 0 0 20px; padding: 0; border: 0; }
.choice-group legend { width: 100%; margin-bottom: 4px; }
.choice-group label { position: relative; cursor: pointer; }
.choice-group input { position: absolute; opacity: 0; }
.choice-group span {
  display: block;
  min-width: 84px;
  padding: 10px 14px;
  border: 1px solid var(--line);
  color: var(--muted);
  background: var(--white);
  text-align: center;
  font-size: 13px;
  font-weight: 700;
}
.choice-group input:checked + span { border-color: var(--blue); color: var(--blue); background: rgba(18, 109, 166, .08); box-shadow: inset 0 -2px var(--blue); }
.choice-group input:focus-visible + span { outline: 2px solid var(--blue); outline-offset: 2px; }
.milestone-fields { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.gate-rule { display: flex; gap: 14px; margin-top: 16px; padding: 12px 14px; border-left: 3px solid var(--blue); background: var(--paper); font-size: 12px; }
.gate-rule b { color: var(--ink); }
.gate-rule span { color: var(--muted); }

.preview-summary { display: grid; grid-template-columns: repeat(4, 1fr); border: 1px solid var(--line); }
.preview-summary div { display: grid; gap: 4px; padding: 14px 16px; border-left: 1px solid var(--line); }
.preview-summary div:first-child { border-left: 0; }
.preview-summary span { color: var(--muted); font-size: 12px; }
.preview-summary strong { color: var(--ink); font: 800 22px var(--display); }
.preview-context { display: flex; justify-content: space-between; gap: 16px; margin: 18px 0 10px; }
.preview-context b { color: var(--ink); }
.preview-context span { color: var(--muted); font-size: 12px; }
.gate-preview {
  display: grid;
  /* 旧语义下里程碑有 13 个，固定六列放不下，改为自适应换行 */
  grid-template-columns: repeat(auto-fill, minmax(124px, 1fr));
  margin: 0 0 10px;
  padding: 0;
  border: 1px solid var(--line);
  list-style: none;
}
.gate-preview li { position: relative; display: grid; gap: 3px; min-width: 0; padding: 11px 10px; border-top: 3px solid transparent; border-left: 1px solid var(--line); }
.gate-preview li.kind-gate { border-top-color: var(--blue); }
.gate-preview li.kind-customer { border-top-color: var(--amber, #d98324); }
.gate-preview li.kind-internal { border-top-color: var(--line); }
.gate-preview b { color: var(--blue); font: 800 11px var(--display); }
.gate-preview span { display: flex; align-items: center; gap: 4px; overflow: hidden; color: var(--ink); font-size: 12px; font-weight: 700; }
.gate-preview span > :first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.kind-tag { flex: none; padding: 0 4px; border-radius: 3px; font-size: 12px; font-style: normal; font-weight: 700; }
.kind-tag.customer { background: #fdf0e0; color: #a35b12; }
.kind-tag.internal { background: var(--paper); color: var(--muted); }
.gate-preview time { display: flex; align-items: center; gap: 4px; color: var(--muted); font-size: 12px; }
.gate-preview time.derived { color: #a35b12; }
.gate-preview time small { padding: 0 3px; border: 1px dashed currentColor; border-radius: 3px; font-size: 12px; }
.derived-hint { margin: 0 0 12px; padding: 8px 10px; border-left: 3px solid #d98324; background: #fdf6ec; color: #7a4a10; font-size: 12px; line-height: 1.6; }
.milestone-hint { margin: 8px 0 0; color: var(--muted); font-size: 12px; line-height: 1.6; }
/* 关键日期分组标题：客户节点 vs 内部节点（试用反馈 0818） */
.ms-band { display: flex; align-items: baseline; gap: 8px; margin: 12px 0 6px; flex-wrap: wrap; }
.ms-band:first-of-type { margin-top: 0; }
.ms-band b { font-size: 13px; }
.ms-band small { color: var(--muted); font-size: 12px; }
.ms-band-customer b { color: var(--blue); }
.stage-preview { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; margin: 0; padding: 0; list-style: none; }
.stage-preview li { display: grid; gap: 6px; padding: 13px; border: 1px solid var(--line); background: var(--white); }
.stage-preview li > div { display: flex; align-items: baseline; gap: 8px; }
.stage-preview li b { color: var(--blue); font: 800 12px var(--display); }
.stage-preview li span { color: var(--ink); font-size: 13px; font-weight: 700; }
.stage-preview small,
.stage-preview time { color: var(--muted); font-size: 12px; }
.preview-warnings { display: grid; gap: 5px; margin-top: 12px; padding: 12px 14px; border-left: 3px solid var(--warning); background: rgba(196, 133, 35, .07); }
.preview-warnings b { color: var(--ink); font-size: 12px; }
.preview-warnings span { color: var(--muted); font-size: 12px; }
.confirmation { display: flex; gap: 10px; margin-top: 18px; color: var(--ink); font-size: 13px; cursor: pointer; }
.confirmation input { width: 18px; height: 18px; flex: 0 0 auto; accent-color: var(--blue); }

@media (max-width: 900px) {
  .wizard-fields,
  .stage-preview { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .field-wide { grid-column: span 1; }
  .scope-layout { grid-template-columns: 1fr; }
  .gate-preview { grid-template-columns: repeat(auto-fill, minmax(112px, 1fr)); }
}

@media (max-width: 600px) {
  .wizard-header { padding: 18px 14px; }
  .wizard-header p { display: none; }
  .wizard-steps li { min-height: 48px; padding: 0 8px; gap: 6px; }
  .wizard-steps span { font-size: 12px; }
  .wizard-form,
  .wizard-preview { padding: 16px 14px; }
  .wizard-fields,
  .stage-preview,
  .preview-summary { grid-template-columns: 1fr; }
  .gate-preview { display: flex; overflow-x: auto; }
  .gate-preview li { min-width: 122px; }
  .field-full { grid-column: auto; }
  .preview-summary div { border-top: 1px solid var(--line); border-left: 0; }
  .preview-summary div:first-child { border-top: 0; }
  .preview-context,
  .wizard-actions { align-items: stretch; flex-direction: column; }
  .wizard-actions .button { width: 100%; }
  .milestone-fields { grid-template-columns: 1fr; }
  .gate-rule { flex-direction: column; gap: 3px; }
}
</style>
